import { useToastStore } from '../stores/useToastStore'
import type {
  ToastOptions,
  ToastPromiseMessages,
  ToastPromiseOptions,
} from '../types/toast.types'

export interface UseToastReturn {
  toast: (message: string, options?: ToastOptions) => string
  success: (message: string, options?: Omit<ToastOptions, 'type'>) => string
  error: (message: string, options?: Omit<ToastOptions, 'type'>) => string
  warning: (message: string, options?: Omit<ToastOptions, 'type'>) => string
  info: (message: string, options?: Omit<ToastOptions, 'type'>) => string
  promise: <T>(
    promise: Promise<T>,
    messages: ToastPromiseMessages<T>,
    options?: ToastPromiseOptions
  ) => Promise<T>
  dismiss: (id: string) => void
  dismissAll: () => void
}

export function useToast(): UseToastReturn {
  const store = useToastStore()

  function toast(message: string, options: ToastOptions = {}): string {
    return store.add(message, options)
  }

  function success(message: string, options: Omit<ToastOptions, 'type'> = {}): string {
    return store.add(message, { ...options, type: 'success' })
  }

  function error(message: string, options: Omit<ToastOptions, 'type'> = {}): string {
    return store.add(message, { ...options, type: 'error' })
  }

  function warning(message: string, options: Omit<ToastOptions, 'type'> = {}): string {
    return store.add(message, { ...options, type: 'warning' })
  }

  function info(message: string, options: Omit<ToastOptions, 'type'> = {}): string {
    return store.add(message, { ...options, type: 'info' })
  }

  async function promise<T>(
    promiseToAwait: Promise<T>,
    messages: ToastPromiseMessages<T>,
    options: ToastPromiseOptions = {}
  ): Promise<T> {
    const toastId = store.add(messages.loading, {
      ...options,
      type: 'info',
      duration: false,
      closable: false,
    })

    try {
      const result = await promiseToAwait

      const successMessage =
        typeof messages.success === 'function' ? messages.success(result) : messages.success

      store.update(toastId, {
        message: successMessage,
        type: 'success',
        duration: options.successDuration ?? store.config.defaultDuration,
        closable: true,
        icon: 'mdi-check-circle',
        createdAt: Date.now(),
        isPaused: false,
      })

      return result
    } catch (err) {
      const errorMessage =
        typeof messages.error === 'function' ? messages.error(err) : messages.error

      store.update(toastId, {
        message: errorMessage,
        type: 'error',
        duration: options.errorDuration ?? store.config.defaultDuration,
        closable: true,
        icon: 'mdi-alert-circle',
        createdAt: Date.now(),
        isPaused: false,
      })

      throw err
    }
  }

  function dismiss(id: string): void {
    store.remove(id)
  }

  function dismissAll(): void {
    store.clear()
  }

  return {
    toast,
    success,
    error,
    warning,
    info,
    promise,
    dismiss,
    dismissAll,
  }
}
